import { Component, effect, inject, input } from '@angular/core';

import { EChartsOption, SeriesOption } from 'echarts';
import { NgxEchartsDirective, provideEcharts } from 'ngx-echarts';

import { VisualBaseComponent } from '../visual-base.component';
import { QuarterlyInstitution } from '../../services/metric.service';
import { StateService } from '../../services/state.service';
import { VisualService } from '../../services/visual.service';
import { Institution } from '../../services/institution.service';
import { GlobalConstants } from '../../../shared/global-constants';

@Component({
  selector: 'app-metric-quarterly-institution',
  standalone: true,
  imports: [NgxEchartsDirective],
  templateUrl: './metric-quarterly-institution.component.html',
  styleUrl: './metric-quarterly-institution.component.scss',
  providers: [provideEcharts()]
})
export class MetricQuarterlyInstitutionComponent extends VisualBaseComponent {
  metric = input<QuarterlyInstitution | undefined>();

  readonly institution = inject(StateService).institution;
  readonly peerInstitutions = inject(StateService).peerInstitutions;
  readonly date = inject(StateService).date;

  override initOptions = { height: 150 }
  override baseOptions: EChartsOption = {
    grid: {
      left: 10,
      right: 10,
      top: 30,
      bottom: 10,
      containLabel: true
    },
    legend: {
      show: true
    },
    xAxis: {
      type: 'category',
      show: true,
      axisLabel: {
        color: '#243b5b'
      },
      data: []
    },
    yAxis: {
      type: 'value',
      show: true,
      axisLabel: {
        color: '#243b5b'
      },
      splitLine: {
        lineStyle: {
          color: '#ff0000'
        }
      }
    },
    axisLabel: {
      color: '#002f8e'
    }
  };
  override tooltipOptions: EChartsOption = {
    tooltip: {
      trigger: 'axis',
      valueFormatter: value => value != null ? VisualService.getValueDisplay(Number(value), this.metric()?.displayType) : '--'
    },
    yAxis: {
      type: 'value',
      show: true,
      axisLabel: {
        color: '#243b5b',
        formatter: (value: number) => this.getYAxisDisplay(this.metric()?.displayType, Number(value))
      },
      splitLine: {
        lineStyle: {
          color: '#ccc'
        }
      }
    }
  };
  override loadingColor = '#ebebeb';
  override colors: string[] = GlobalConstants.lineColors;

  constructor() {
    super();

    effect(() => {
      const peerInstitutions = this.peerInstitutions();
      const date = this.date();
      const metric = this.metric();
      if (this.chartInit() && peerInstitutions && date && metric) {
        this.updateVisual(peerInstitutions, date, metric);
      }
    });
  }

  public updateVisual(peerInstitutions: Institution[], date: Date, metric: QuarterlyInstitution) {
    this.metricService.getMetricInstitutionsData(this.institution().iid, metric.metricId, peerInstitutions?.map(i => i.iid))?.subscribe(data => {
      const dateRange = { startDate: new Date(new Date(date).setFullYear(new Date(date).getFullYear() - 5)), endDate: date };
      const dataSeries = this.visualService.getMetricSeriesInstitutions(this.metric()?.displayType, data, peerInstitutions, dateRange);

      const series: SeriesOption[] = [];
      series.push({
        name: this.institution()?.name,
        data: dataSeries.institutionSeries,
        color: this.colors[0],
        type: 'line',
        symbol: 'line',
        symbolSize: 0
      });
      dataSeries.peerInstitutionSeries.forEach((peerInstitution, index) => {
        series.push({
          name: peerInstitution.institution.name,
          data: peerInstitution.series,
          color: this.colors[index + 1],
          type: 'line',
          symbol: 'line',
          symbolSize: 0
        });
      });

      if (dataSeries.institutionSeries.filter(v => v != null).length == 0) {
        this.echartsInstance?.clear();
        this.chartOptions = this.noDataOptions;
      }
      else {
        this.updateOptions = {
          xAxis: {
            data: dataSeries.dateSeries
          },
          series: series
        };
      }
      this.echartsInstance?.hideLoading();
    });
  }

  private getYAxisDisplay(displayType: string | undefined = '', value: number): string {
    const formatSymbol = (displayType == 'USD') ? 'M' : (displayType == 'PCT') ? '%' : '';
    return (displayType == 'USD' ? value.toLocaleString() : value.toFixed(1)) + formatSymbol;
  }
}
